import { forwardRef, useEffect, useState } from "react";
import { getPalette } from "../../themes/index.js";
import { urlFor } from "../../firebase/storage.js";
import { getBackground } from "../../backgrounds/registry.js";

export const SOCIAL_CARD_WIDTH = 1200;
export const SOCIAL_CARD_HEIGHT = 630;

function assetUrl(path) {
  if (!path) return "";
  if (/^(https?:)?\/\//.test(path) || path.startsWith("data:") || path.startsWith("/")) return path;
  return urlFor(path);
}

function readImageAsDataURL(src) {
  return fetch(src)
    .then((res) => {
      if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
      return res.blob();
    })
    .then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }));
}

async function prepareClone(node) {
  const clone = node.cloneNode(true);
  const sourceImages = Array.from(node.querySelectorAll("img"));
  const cloneImages = Array.from(clone.querySelectorAll("img"));

  await Promise.all(sourceImages.map(async (img, index) => {
    if (img.decode) await img.decode().catch(() => {});
    const src = img.currentSrc || img.src;
    if (!src) return;
    try {
      cloneImages[index].setAttribute("src", await readImageAsDataURL(src));
    } catch {
      cloneImages[index].setAttribute("src", src);
    }
  }));

  return clone;
}

function waitForFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function waitForCardReady(node) {
  for (let i = 0; i < 90; i += 1) {
    if (node.dataset.socialCardReady === "1") return;
    await waitForFrame();
  }
}

export async function captureSocialCard(node) {
  if (!node) throw new Error("Social card node is unavailable");
  await waitForCardReady(node);
  if (document.fonts?.ready) await document.fonts.ready;
  await waitForFrame();
  await waitForFrame();

  const clone = await prepareClone(node);
  clone.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  const markup = new XMLSerializer().serializeToString(clone);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${SOCIAL_CARD_WIDTH}" height="${SOCIAL_CARD_HEIGHT}" viewBox="0 0 ${SOCIAL_CARD_WIDTH} ${SOCIAL_CARD_HEIGHT}">
  <foreignObject width="100%" height="100%">${markup}</foreignObject>
</svg>`;

  const image = new Image();
  image.decoding = "async";
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();

  const canvas = document.createElement("canvas");
  canvas.width = SOCIAL_CARD_WIDTH;
  canvas.height = SOCIAL_CARD_HEIGHT;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not encode social card"));
    }, "image/png");
  });
}

function DynamicBackground({ backgroundId, backgroundSetId, onReady }) {
  const [Background, setBackground] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const meta = getBackground(backgroundId);
    setBackground(null);
    if (!meta) {
      onReady(true);
      return () => { cancelled = true; };
    }

    onReady(false);
    meta.load().then((mod) => {
      if (cancelled) return;
      setBackground(() => mod.default);
      requestAnimationFrame(() => onReady(true));
    }).catch(() => {
      if (!cancelled) onReady(true);
    });
    return () => { cancelled = true; };
  }, [backgroundId, onReady]);

  if (!Background) return null;
  const meta = getBackground(backgroundId);
  const setId = backgroundSetId ?? meta?.defaultSetId;
  return <Background setId={setId} style={{ position: "absolute", inset: 0 }} />;
}

const SocialCardPreview = forwardRef(function SocialCardPreview({ event, og, imageUrls = [] }, ref) {
  const details = event?.details || {};
  const theme = event?.theme || {};
  const palette = getPalette(event?.theme?.paletteId);
  const [backgroundReady, setBackgroundReady] = useState(!theme.backgroundId);

  useEffect(() => {
    if (!theme.backgroundId) setBackgroundReady(true);
  }, [theme.backgroundId]);

  const title = details.title || event?.slug || "You're invited";
  const subtitle = details.subtitle || "";
  const meta = og?.description || "";
  const extra = [details.accreditedBy, details.cmeCredits].filter(Boolean).join("  ·  ");
  const cover = assetUrl(details.heroImage) || imageUrls[0] || assetUrl(event?.imagePages?.[0]) || "";

  const rootStyle = {
    width: SOCIAL_CARD_WIDTH,
    height: SOCIAL_CARD_HEIGHT,
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(180deg, ${palette.bgSoft}, ${palette.bg})`,
    color: palette.text,
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  };
  const glowStyle = {
    position: "absolute",
    left: 760,
    top: -120,
    width: 520,
    height: 420,
    borderRadius: "50%",
    background: palette.accent,
    opacity: 0.28,
    filter: "blur(80px)",
    zIndex: 2,
  };
  const contentStyle = {
    position: "absolute",
    left: 72,
    top: 66,
    width: 580,
    zIndex: 5,
  };

  return (
    <div ref={ref} style={rootStyle} data-social-card-ready={backgroundReady ? "1" : "0"}>
      {theme.backgroundId && (
        <DynamicBackground
          backgroundId={theme.backgroundId}
          backgroundSetId={theme.backgroundSetId}
          onReady={setBackgroundReady}
        />
      )}
      <div style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        background: theme.backgroundId
          ? `linear-gradient(90deg, ${palette.bg} 0%, ${palette.bg} 34%, transparent 100%)`
          : "transparent",
      }} />
      <div style={glowStyle} />
      {cover && (
        <div style={{
          position: "absolute",
          left: 700,
          top: 0,
          width: 500,
          height: 630,
          overflow: "hidden",
          zIndex: 3,
        }}>
          <img
            src={cover}
            alt=""
            crossOrigin="anonymous"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "brightness(.86)" }}
          />
        </div>
      )}
      {cover && (
        <div style={{
          position: "absolute",
          left: 700,
          top: 0,
          width: 230,
          height: 630,
          zIndex: 4,
          background: `linear-gradient(90deg, ${palette.bg} 0%, ${palette.bg} 8%, transparent 100%)`,
        }} />
      )}
      <div style={contentStyle}>
        <div style={{ color: palette.accent, fontSize: 16, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase" }}>
          EINVITE.TO
        </div>
        <div style={{ width: 52, height: 4, background: palette.accent, marginTop: 8, marginBottom: 72 }} />
        <div style={{
          fontSize: 76,
          lineHeight: 0.98,
          fontWeight: 800,
          letterSpacing: 0,
          maxHeight: 226,
          overflow: "hidden",
        }}>
          {title}
        </div>
        {subtitle && subtitle !== meta && (
          <div style={{ color: palette.muted, fontSize: 28, lineHeight: 1.25, marginTop: 34, maxHeight: 70, overflow: "hidden" }}>
            {subtitle}
          </div>
        )}
        {meta && (
          <div style={{ color: palette.muted, fontSize: 24, lineHeight: 1.35, marginTop: subtitle && subtitle !== meta ? 18 : 38 }}>
            {meta}
          </div>
        )}
        {extra && (
          <div style={{ color: palette.muted, fontSize: 24, lineHeight: 1.35, marginTop: 8 }}>
            {extra}
          </div>
        )}
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, width: 1200, height: 4, background: palette.accent, zIndex: 6 }} />
    </div>
  );
});

export default SocialCardPreview;
